/**
 * Authentication sagas.
 * 
 * Responsible for the process of logging in, refreshing tokens and logging out.
 */

import { take, takeEvery, call, put } from 'redux-saga/effects'
import * as actions from './actions'
import { authenticate, logOut } from './functions'
import { SagaIterator } from 'redux-saga'
import { store } from 'modules/root'
import { FirebaseError } from 'firebase/app'
import { browserLocalPersistence, getAuth, setPersistence, User } from 'firebase/auth'

import { readyAction } from 'modules/root/actions'
import platform from 'modules/platform'
import { anythingRequiresUpload } from 'modules/database/selectors'
import { notifyAuthInitialized } from 'modules/auth'

function* handleLogin(action: actions.LoginAction): SagaIterator {
	console.log('AUTH: handleLogin')
	try {
		yield call(authenticate, action.payload.username, action.payload.password)
	} catch (error) {
		yield put(actions.loginError(error as FirebaseError))
	}
}

function* handleLogout(): SagaIterator {
	console.log('AUTH: handleLogout')

	const requiresUpload = (yield call(anythingRequiresUpload)) as boolean
	if (requiresUpload) {
		platform.alert('You have unsynced changes. Please go online to sync your changes before you sign out of your account.', 'Unsynced Changes')
		return
	}

	yield call(logOut)
}

export default function* authSaga(): SagaIterator {
	/* Wait for the state to be ready, as we read the state in the loggedIn function.
	The state isn't immediately ready, as we use react-persist to load persisted state,
	which happens asynchronously.
	*/
	yield take(readyAction)
	yield takeEvery(actions.login, handleLogin)
	yield takeEvery(actions.logout, handleLogout)

	function handleAuthStateChanged(user: User | null) {
		console.log('AUTH: auth state changed: logged in', user !== null)
		if (user) {
			// User is signed in.
			store.dispatch(actions.loggedIn(user))
		} else {
			// User is signed out.
			store.dispatch(actions.loggedOut())
		}
		notifyAuthInitialized()
	}
	const auth = getAuth()

	console.log('AUTH: listening to auth state changed')
	auth.onAuthStateChanged(handleAuthStateChanged)
}
